import React, { useEffect, useRef, useCallback, useState } from 'react';
import ReactDOM from 'react-dom/client';
import useSignalingStore from 'views/signalingStore';
import useUserId from 'hooks/useUserId';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  shouldIgnoreCall, 
  trackOutgoingCall, 
  trackActiveCall, 
  cleanupCall 
} from 'utils/callTracker';
import ringtone from '../assets/sound/ringtone.mp3';

// MUI imports
import { 
  Dialog, 
  DialogContent, 
  Avatar, 
  Typography, 
  Button, 
  Box, 
  IconButton,
  Paper,
  useTheme,
  alpha,
  keyframes,
  styled,
  Backdrop
} from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
import CallEndIcon from '@mui/icons-material/CallEnd';

// Custom hook for ringtone management
const useRingtone = () => {
  const audioRef = useRef(null);
  const [isEnabled, setIsEnabled] = useState(false);
  
  // Function to create a fallback tone using AudioContext
  const createFallbackTone = useCallback(() => {
    try {
      if (typeof AudioContext !== 'undefined' || typeof webkitAudioContext !== 'undefined') {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioContextClass();
        
        // Create a simple beep sound
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.type = 'sine';
        oscillator.frequency.value = 800; // Set frequency
        gainNode.gain.value = 0.5; // Set volume
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        return {
          play: () => {
            oscillator.start();
            return Promise.resolve();
          },
          stop: () => {
            try {
              oscillator.stop();
            } catch (e) {
              // Ignore errors when stopping
            }
          },
          loop: true
        };
      }
    } catch (e) {
      console.error('Failed to create fallback tone:', e);
    }
    
    return null;
  }, []);
  
  // Function to initialize audio
  const initializeAudio = useCallback(() => {
    if (audioRef.current) {
      return true; // Already initialized
    }
    
    try {
      // Try to create the audio object with the imported ringtone
      const audio = new Audio(ringtone);
      audio.loop = true;
      audio.preload = 'auto';
      audioRef.current = audio;
      setIsEnabled(true);
      return true;
    } catch (error) {
      console.error('Failed to initialize audio with ringtone, trying fallback:', error);
      
      // Try to create a fallback tone
      const fallbackTone = createFallbackTone();
      if (fallbackTone) {
        audioRef.current = fallbackTone;
        setIsEnabled(true);
        return true;
      }
      
      return false;
    }
  }, [createFallbackTone]);
  
  // Initialize audio on first user interaction
  useEffect(() => {
    const handleUserInteraction = () => {
      const success = initializeAudio();
      
      if (success) {
        // Remove listeners after successful initialization
        document.removeEventListener('click', handleUserInteraction);
        document.removeEventListener('touchstart', handleUserInteraction);
        document.removeEventListener('keydown', handleUserInteraction);
      }
    };
    
    // Add event listeners for user interaction
    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);
    
    return () => {
      // Clean up event listeners
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      
      // Clean up audio if it was created
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.src = '';
        } catch (e) {
          console.error('Error cleaning up audio:', e);
        }
      }
    };
  }, [initializeAudio]);
  
  // Safe play method
  const play = useCallback(() => {
    // Try to initialize audio if not already done
    if (!audioRef.current) {
      const success = initializeAudio();
      if (!success) {
        return Promise.reject(new Error('Audio initialization failed'));
      }
    }
    
    try {
      audioRef.current.currentTime = 0;
      return audioRef.current.play();
    } catch (error) {
      console.error('Error playing ringtone:', error);
      return Promise.reject(error);
    }
  }, [initializeAudio]);
  
  // Safe stop method
  const stop = useCallback(() => {
    if (!audioRef.current) {
      return;
    }
    
    try {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    } catch (error) {
      console.error('Error stopping ringtone:', error);
    }
  }, []);
  
  return { play, stop, isEnabled, initializeAudio };
};

// Define keyframes animations
const pulseLight = keyframes`
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
    opacity: 0;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
    opacity: 0;
  }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1); opacity: 0.8; }
`;

const wave = keyframes`
  0% { height: 8px; }
  50% { height: 24px; }
  100% { height: 8px; }
`;

const shadowPulse = keyframes`
  0% { box-shadow: 0 0 8px 2px ${alpha('#0017E7', 0.3)}; }
  50% { box-shadow: 0 0 16px 4px ${alpha('#E700C8', 0.5)}; }
  100% { box-shadow: 0 0 8px 2px ${alpha('#0017E7', 0.3)}; }
`;

// Styled components
const PulsingContainer = styled(Box)({
  position: 'absolute',
  overflow: 'hidden',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  pointerEvents: 'none',
  zIndex: 0,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgb(255, 255, 255) 0%, rgba(255, 255, 255, 0.73) 100%)',
    animation: `${pulseLight} 2.5s infinite`,
    width: '200%',
    height: '200%',
    zIndex: 0,
    pointerEvents: 'none'
  }
});

const AvatarContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginBottom: '30px',
  width: 110,
  height: 110,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  // overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '50%',
    padding: '3px',
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    animation: `${pulse} 2s ease-in-out infinite`,
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
  }
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  border: `2px solid transparent`,
  animation: `${shadowPulse} 4s ease-in-out infinite`,
  fontSize: '36px',
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  position: 'relative',
  zIndex: 2
}));

const WaveContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '4px',
  marginBottom: '16px',
  height: '30px'
});

const WaveBar = styled(Box)(({ theme, index }) => ({
  width: '4px',
  height: '12px',
  backgroundColor: theme.palette.primary.main,
  borderRadius: '2px',
  animation: `${wave} 1.2s ease-in-out infinite`,
  animationDelay: `${index * 0.15}s`
}));

const ActionButton = styled(IconButton)(({ theme, color }) => ({
  width: '64px',
  height: '64px',
  backgroundColor: color === 'primary' 
    ? alpha(theme.palette.primary.main, 0.2) 
    : alpha(theme.palette.error.main, 0.2),
  '&:hover': {
    backgroundColor: color === 'primary' 
      ? alpha(theme.palette.primary.main, 0.6) 
      : alpha(theme.palette.error.main, 0.6),
    transform: 'translateY(-5px)',
  },
  transition: 'all 0.3s ease',
}));

// Call Modal Component
const CallModal = ({ callData, onAccept, onDecline }) => {
  const theme = useTheme();
  
  return (
    <Dialog 
      open={true} 
      maxWidth="xs" 
      fullWidth 
      BackdropComponent={(props) => (
        <Backdrop
          {...props}
          sx={{
            backdropFilter: 'blur(5px)',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            transition: 'backdrop-filter 0.3s, background-color 0.3s',
          }}
        />
      )}
      PaperComponent={(props) => (
        <Paper 
          {...props} 
          sx={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(16px)',
            borderRadius: 6,
            border: '1px solid rgba(255, 255, 255, 0.4)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            overflow: 'hidden',
            position: 'relative',
          }} 
        />
      )}
      PaperProps={{
        elevation: 24
      }}
    >
      <PulsingContainer />
      <DialogContent sx={{ 
        textAlign: 'center', 
        py: 4, 
        position: 'relative',
        zIndex: 1,
        background: 'transparent'
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* Avatar with pulse animation */}
          <Box sx={{ position: 'relative', mb: 3, display: 'flex', justifyContent: 'center' }}>
            <AvatarContainer>
              <StyledAvatar 
                src={callData.sender_info?.image} 
                alt={callData.sender_info?.first_name || 'Caller'}
              >
                {callData.sender_info?.first_name?.charAt(0) || '?'}
              </StyledAvatar>
            </AvatarContainer>
          </Box>
          
          {/* Call status indicator with wave animation */}
          <Typography sx={{
            fontFamily: 'Poppins',
            fontWeight: '600',
            fontSize: '24px',
            color: '#000000',
            textAlign: 'center',
            marginBottom: '10px',
          }}>
            Incoming Call
          </Typography>
          
          <Typography
          
          sx={{
            fontFamily: 'Poppins',
            fontWeight: '400',
            fontSize: '16px',
            color: '#000000',
            textAlign: 'center',
            marginBottom: '10px',
          }}>
            {(callData.sender_info?.first_name || 'Someone').charAt(0).toUpperCase() + (callData.sender_info?.first_name || 'Someone').slice(1)} 
            {callData.sender_info?.last_name ? ` ${callData.sender_info.last_name.charAt(0).toUpperCase() + callData.sender_info.last_name.slice(1)}` : ''}
          </Typography>
          
          <WaveContainer>
            {[0, 1, 2, 3].map((index) => (
              <WaveBar key={index} index={index} />
            ))}
          </WaveContainer>
          
          {/* Call actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-around', width: '100%', mt: 2, gap: 3 }}>
            <ActionButton 
              color="error" 
              onClick={onDecline}
              aria-label="Decline call"
            >
              <CallEndIcon fontSize="large" />
            </ActionButton>
            
            <ActionButton 
              color="primary" 
              onClick={onAccept}
              aria-label="Accept call"
            >
              <CallIcon fontSize="large" />
            </ActionButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// Main WebSocketManager Component
const WebSocketManager = () => {
  console.log('🔄 WebSocketManager component rendered');

  const [activeCall, setActiveCall] = useState(null);
  const wsRef = useRef(null);
  const notifMsgWsRef = useRef(null);
  const chatWsRef = useRef(null);
  const ringWsRef = useRef(null);
  const userId = useUserId();
  const navigate = useNavigate();

  const pendingCallNotifications = useRef([]);
  const isDomReady = useRef(document.readyState === 'complete');

  // Connection state tracking
  const [connectionStates, setConnectionStates] = useState({
    notif: 'disconnected',
    msgNotif: 'disconnected',
    chat: 'disconnected',
    ring: 'disconnected'
  });

  // Flag to prevent multiple initializations
  const isInitialized = useRef(false);
  const currentUserId = useRef(null);

  // Enhanced duplicate message tracking
  const messageDeduplicationMap = useRef(new Map());
  const processedMessageIds = useRef(new Set());

  // Get store functions first (before using them in callbacks)
  const {
    setSignalingServer,
    setSignalingServer2,
    setSignalingServer3,
    addMessage,
    addNotification,
    updateNotificationCount,
    addMessageNotification,
    initializeNotifications,
    fetchUnreadNotifications,
  } = useSignalingStore();

  // Test function to manually trigger a notification (for debugging)
  const testNotification = useCallback(() => {
    console.log('🧪 Testing notification display...');
    const testNotif = {
      id: Date.now(),
      content: 'Test notification - WebSocket is working!',
      timestamp: new Date().toISOString(),
      read: false,
      type: 'notif',
      sender_id: userId.userId,
      sender_name: 'Test System',
      sender_image: null
    };
    addNotification(testNotif);
    console.log('🧪 Test notification added:', testNotif);

    // Debug: Check store state after adding
    setTimeout(() => {
      const { notifications } = useSignalingStore.getState();
      console.log('🔍 Store state after test notification:', {
        total: notifications.length,
        latest: notifications[notifications.length - 1]
      });
    }, 100);
  }, [userId.userId, addNotification]);

  // Test function to simulate a WebSocket notification message
  const testWebSocketNotification = useCallback(() => {
    console.log('🧪 Testing WebSocket notification simulation...');

    // Simulate a notification message like it would come from WebSocket
    const mockNotificationData = {
      type: 'notif',
      id: Date.now(),
      notif: 'Test WebSocket notification - this should show a snackbar!',
      content: 'Test WebSocket notification - this should show a snackbar!',
      timestamp: new Date().toISOString(),
      sender_id: userId.userId,
      sender_name: 'WebSocket Test',
      sender_image: null,
      link: null
    };

    // For testing, skip duplicate check and directly add notification
    const notification = {
      id: mockNotificationData.id,
      content: mockNotificationData.notif || mockNotificationData.content,
      timestamp: mockNotificationData.timestamp,
      link: mockNotificationData.link,
      read: false,
      type: mockNotificationData.type,
      sender_id: mockNotificationData.sender_id,
      sender_name: mockNotificationData.sender_name,
      sender_image: mockNotificationData.sender_image
    };

    console.log('🔔 Adding simulated WebSocket NOTIFICATION to store:', notification);
    addNotification(notification);
    updateNotificationCount(mockNotificationData.sender_id || userId.userId);
  }, [userId.userId, addNotification, updateNotificationCount]);

  // Test function to check WebSocket connections
  const testWebSocketConnections = useCallback(() => {
    console.log('🧪 Testing WebSocket connections...');

    const connections = [
      { name: 'Notification', ref: wsRef, server: 'signalingServer' },
      { name: 'Message Notification', ref: notifMsgWsRef, server: 'signalingServer2' },
      { name: 'Chat', ref: chatWsRef, server: 'chat' },
      { name: 'Ring', ref: ringWsRef, server: 'signalingServer3' }
    ];

    connections.forEach(({ name, ref }) => {
      const ws = ref.current;
      if (ws) {
        console.log(`🔗 ${name} WebSocket:`, {
          readyState: ws.readyState,
          url: ws.url,
          status: ws.readyState === WebSocket.OPEN ? 'CONNECTED' :
                  ws.readyState === WebSocket.CONNECTING ? 'CONNECTING' :
                  ws.readyState === WebSocket.CLOSING ? 'CLOSING' : 'CLOSED'
        });
      } else {
        console.log(`❌ ${name} WebSocket: NOT INITIALIZED`);
      }
    });

    // Test the store connections
    const { signalingServer, signalingServer2, signalingServer3 } = useSignalingStore.getState();
    console.log('🏪 Store WebSocket references:', {
      signalingServer: signalingServer ? 'SET' : 'NULL',
      signalingServer2: signalingServer2 ? 'SET' : 'NULL',
      signalingServer3: signalingServer3 ? 'SET' : 'NULL'
    });

    return {
      notificationWS: wsRef.current?.readyState === WebSocket.OPEN,
      messageNotifWS: notifMsgWsRef.current?.readyState === WebSocket.OPEN,
      chatWS: chatWsRef.current?.readyState === WebSocket.OPEN,
      ringWS: ringWsRef.current?.readyState === WebSocket.OPEN,
      storeConnections: {
        signalingServer: !!signalingServer,
        signalingServer2: !!signalingServer2,
        signalingServer3: !!signalingServer3
      }
    };
  }, []);

  // Expose test functions to window for debugging
  useEffect(() => {
    // @ts-ignore - Adding test functions to window for debugging
    window.testNotification = testNotification;
    window.testWebSocketNotification = testWebSocketNotification;
    window.testWebSocketConnections = testWebSocketConnections;
    console.log('🧪 Test functions exposed: window.testNotification(), window.testWebSocketNotification(), window.testWebSocketConnections()');
  }, [testNotification, testWebSocketNotification, testWebSocketConnections]);

  // Reconnection timers
  const reconnectTimers = useRef({});
  const reconnectAttempts = useRef({
    notif: 0,
    msgNotif: 0,
    chat: 0,
    ring: 0
  });

  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 1000; // 1 second

  // Heartbeat mechanism
  const heartbeatInterval = useRef(null);
  const lastHeartbeat = useRef({
    notif: Date.now(),
    msgNotif: Date.now(),
    chat: Date.now(),
    ring: Date.now()
  });

  // Store functions already declared above

  const { play, stop, isEnabled, initializeAudio } = useRingtone();

  // Helper function to check for duplicate messages
  const isDuplicateMessage = useCallback((data, source) => {
    const messageKey = `${data.sender_id}_${data.message || data.notif || data.content}_${data.timestamp}`;
    const messageId = data.id || messageKey;

    // Check if we've already processed this exact message ID
    if (processedMessageIds.current.has(messageId)) {
      console.log(`🚫 Duplicate message ID detected from ${source}:`, messageId);
      return true;
    }

    // Check if we've recently processed a similar message
    const now = Date.now();
    const existingEntry = messageDeduplicationMap.current.get(messageKey);

    if (existingEntry && (now - existingEntry.timestamp) < 5000) { // 5 second window
      console.log(`🚫 Duplicate message detected from ${source} within 5s window:`, messageKey);
      return true;
    }

    // Mark as processed
    processedMessageIds.current.add(messageId);
    messageDeduplicationMap.current.set(messageKey, { timestamp: now, source });

    // Clean up old entries (keep only last 100)
    if (processedMessageIds.current.size > 100) {
      const entries = Array.from(processedMessageIds.current);
      const toKeep = entries.slice(-50);
      processedMessageIds.current.clear();
      toKeep.forEach(id => processedMessageIds.current.add(id));
    }

    // Clean up old deduplication entries
    if (messageDeduplicationMap.current.size > 100) {
      const entries = Array.from(messageDeduplicationMap.current.entries());
      const recentEntries = entries.filter(([, entry]) => (now - entry.timestamp) < 30000); // Keep 30s
      messageDeduplicationMap.current.clear();
      recentEntries.forEach(([key, entry]) => messageDeduplicationMap.current.set(key, entry));
    }

    return false;
  }, []);

  // Calculate reconnection delay with exponential backoff
  const getReconnectDelay = (attempts) => {
    return Math.min(baseReconnectDelay * Math.pow(2, attempts), 30000); // Max 30 seconds
  };

  // Clear reconnection timer
  const clearReconnectTimer = (type) => {
    if (reconnectTimers.current[type]) {
      clearTimeout(reconnectTimers.current[type]);
      delete reconnectTimers.current[type];
    }
  };

  // Generic reconnection function
  const scheduleReconnect = (type, createWebSocketFn) => {
    if (reconnectAttempts.current[type] >= maxReconnectAttempts) {
      console.error(`Max reconnection attempts reached for ${type} WebSocket`);
      setConnectionStates(prev => ({ ...prev, [type]: 'failed' }));
      return;
    }

    const delay = getReconnectDelay(reconnectAttempts.current[type]);
    console.log(`Scheduling ${type} WebSocket reconnection in ${delay}ms (attempt ${reconnectAttempts.current[type] + 1})`);

    setConnectionStates(prev => ({ ...prev, [type]: 'reconnecting' }));

    reconnectTimers.current[type] = setTimeout(() => {
      reconnectAttempts.current[type]++;
      createWebSocketFn();
    }, delay);
  };

  // Reset reconnection attempts on successful connection
  const resetReconnectAttempts = (type) => {
    reconnectAttempts.current[type] = 0;
    clearReconnectTimer(type);
    setConnectionStates(prev => ({ ...prev, [type]: 'connected' }));
  };

  // Handle incoming call message
  const handleIncomingCallMessage = useCallback((data, source = 'unknown') => {
    console.log(`Handling incoming call notification from ${source} source:`, data);
    
    if (data.type === "end_ring") {
      if (data.meetingLink) {
        cleanupCall(data.meetingLink);
      }
      sessionStorage.removeItem('incomingCallData');
      setActiveCall(null);
      
      stop(); // Stop the ringtone
      return;
    }
    
    if (shouldIgnoreCall(data, userId.userId)) {
      console.log('Call tracker suggests ignoring this call notification');
      return;
    }
    
    const callDataWithTimestamp = {
      ...data,
      timestamp: new Date().toISOString()
    };
    
    sessionStorage.setItem('incomingCallData', JSON.stringify(callDataWithTimestamp));
    
    const isInChatComponent = window.location.pathname === '/chat';
    
    if (isInChatComponent) {
      window.dispatchEvent(new CustomEvent('incomingCallInChat', { 
        detail: callDataWithTimestamp
      }));
      
      sessionStorage.setItem('incomingCallData', JSON.stringify({
        ...callDataWithTimestamp,
        handledByChat: false
      }));
    } else {
      if (isDomReady.current) {
        setActiveCall(callDataWithTimestamp);
        
        // Attempt to play the ringtone if audio is initialized
        if (isEnabled) {
          play()
            .then(() => console.log('Ring sound playing successfully'))
            .catch(err => console.error("Error playing ring sound:", err));
        } else {
          console.warn("Audio not yet enabled by user interaction, ringtone won't play");
        }
      } else {
        pendingCallNotifications.current.push(callDataWithTimestamp);
      }
    }
    
    addNotification({
      id: Date.now(),
      content: `Incoming call from ${callDataWithTimestamp.sender_info?.first_name || 'someone'}`,
      timestamp: new Date().toISOString(),
      link: '/chat',
      read: false,
      type: 'call',
      sender_id: callDataWithTimestamp.sender_info?.id,
      sender_name: `${callDataWithTimestamp.sender_info?.first_name || ''} ${callDataWithTimestamp.sender_info?.last_name || ''}`.trim() || 'Caller',
      sender_image: callDataWithTimestamp.sender_info?.image || null,
      meetingLink: callDataWithTimestamp.meetingLink || null,
      isCall: true
    });
  }, [addNotification, play, stop, isEnabled, navigate, userId.userId]);

  // Handle call acceptance
  const handleAcceptCall = useCallback((callData) => {
    sessionStorage.setItem('incomingCallData', JSON.stringify(callData));
    
    if (callData.meetingLink) {
      trackActiveCall(callData.meetingLink, callData.receiver_id);
    }
    
    setActiveCall(null);
    stop(); // Stop the ringtone
    
    navigate('/chat', { state: { fromOtherPage: true, incomingCall: callData } });
  }, [navigate, stop]);

  // Handle call decline
  const handleDeclineCall = useCallback(() => {
              sessionStorage.removeItem('incomingCallData');
    setActiveCall(null);
    stop(); // Stop the ringtone
  }, [stop]);

  // Create Notification WebSocket
  const createNotificationWebSocket = useCallback(() => {
    if (!userId.userId) return;

    // Prevent duplicate connections - be more strict
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      console.log('🚫 Notification WebSocket already connected/connecting, skipping creation');
      return wsRef.current;
    }

    // Close existing connection if it exists
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      console.log('🔴 Closing existing notification WebSocket before creating new one');
      wsRef.current.close(1000, 'Creating new connection');
      wsRef.current = null; // Clear immediately instead of using timeout
    }

    const apiUrl = process.env.REACT_APP_API_URL_SOKET || '';
    console.log('Creating Notification WebSocket connection...');

    const notifWs = new WebSocket(`${apiUrl}ws/notif/${userId.userId}/`);
    wsRef.current = notifWs;

    notifWs.onopen = () => {
      console.log('✅ Notification WebSocket connected successfully');
      setSignalingServer(notifWs);
      resetReconnectAttempts('notif');
      setConnectionStates(prev => ({ ...prev, notif: 'connected' }));

      // Debug: Verify the signaling server is set
      console.log('🔗 Notification WebSocket set in store, ready for notifications');
    };

    notifWs.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📨 Notification WebSocket received:', data);

        // Update heartbeat timestamp
        lastHeartbeat.current.notif = Date.now();

        // Skip heartbeat responses
        if (data.type === 'pong') {
          console.log('💓 Received heartbeat pong from notification WebSocket');
          return;
        }

        // Check for duplicates before processing
        if (isDuplicateMessage(data, 'NotificationWS')) {
          return;
        }

        addMessage(data);

        // Handle different types of notifications - ONLY handle actual notifications, NOT messages
        if (data.type === 'notif' || data.type === 'notification') {
          console.log('🔔 Processing NOTIFICATION:', data.type, data);

          const notification = {
            id: data.id || Date.now(),
            content: data.notif || data.content || 'New notification',
            timestamp: data.timestamp || new Date().toISOString(),
            link: data.link,
            read: false,
            type: data.type,
            sender_id: data.sender_id,
            sender_name: data.sender_name || 'System',
            sender_image: data.sender_image || null
          };

          console.log('🔔 Adding NOTIFICATION to store:', notification);
          addNotification(notification);

          // Debug: Check if notification was added to store
          setTimeout(() => {
            const { notifications } = useSignalingStore.getState();
            console.log('🔍 Store notifications after adding:', {
              total: notifications.length,
              latest: notifications[notifications.length - 1]?.content?.substring(0, 30) + '...',
              latestId: notifications[notifications.length - 1]?.id
            });
          }, 100);

          // Update NOTIFICATION count (not message count) - only for actual notifications
          updateNotificationCount(data.sender_id || userId.userId);
          console.log('📊 NOTIFICATION count updated for user:', data.sender_id || userId.userId);

        } else if (data.type === 'notif_message' || data.type === 'notif_from_messages') {
          console.log('💬 Processing MESSAGE NOTIFICATION:', data.type, data);

          // Create unique ID to prevent duplicates
          const messageId = data.id || `notif_msg_${data.sender_id}_${Date.now()}`;

          const messageNotification = {
            id: messageId,
            content: data.message || data.notif || 'New message',
            message: data.message || data.notif || '',
            sender_id: data.sender_id,
            sender_name: data.sender_name || 'User',
            sender_image: data.sender_image || null,
            timestamp: data.timestamp || new Date().toISOString(),
            read: false,
          };

          console.log('💬 Adding MESSAGE NOTIFICATION to store:', messageNotification);
          addMessageNotification(messageNotification);

        } else {
          console.log('ℹ️ Received other message type:', data.type, data);
        }
      } catch (error) {
        console.error('❌ Error processing notification:', error);
      }
    };

    notifWs.onclose = (event) => {
      console.log('🔴 Notification WebSocket closed:', event.code, event.reason || 'No reason provided');
      console.log('🔴 Close event details:', { wasClean: event.wasClean, code: event.code, reason: event.reason });
      setConnectionStates(prev => ({ ...prev, notif: 'disconnected' }));

      // Only attempt reconnection if it wasn't a clean close
      if (event.code !== 1000 && userId.userId) {
        console.log('🔄 Scheduling reconnection for notification WebSocket...');
        scheduleReconnect('notif', createNotificationWebSocket);
      } else {
        console.log('✅ Clean close, not reconnecting notification WebSocket');
      }
    };

    notifWs.onerror = (error) => {
      console.error('Notification WebSocket error:', error);
      setConnectionStates(prev => ({ ...prev, notif: 'error' }));
    };

    return notifWs;
  }, [userId.userId, setSignalingServer, addMessage, addNotification, updateNotificationCount, resetReconnectAttempts, scheduleReconnect, isDuplicateMessage]);

  // Create Message Notification WebSocket
  const createMessageNotificationWebSocket = useCallback(() => {
    if (!userId.userId) return;

    // Prevent duplicate connections - be more strict
    if (notifMsgWsRef.current && (notifMsgWsRef.current.readyState === WebSocket.OPEN || notifMsgWsRef.current.readyState === WebSocket.CONNECTING)) {
      console.log('🚫 Message Notification WebSocket already connected/connecting, skipping creation');
      return notifMsgWsRef.current;
    }

    // Close existing connection if it exists
    if (notifMsgWsRef.current && notifMsgWsRef.current.readyState !== WebSocket.CLOSED) {
      console.log('🔴 Closing existing message notification WebSocket before creating new one');
      notifMsgWsRef.current.close(1000, 'Creating new connection');
      notifMsgWsRef.current = null; // Clear immediately instead of using timeout
    }

    const apiUrl = process.env.REACT_APP_API_URL_SOKET || '';
    console.log('Creating Message Notification WebSocket connection...');

    const msgNotifWs = new WebSocket(`${apiUrl}ws/notif_message/${userId.userId}/`);
    notifMsgWsRef.current = msgNotifWs;

    msgNotifWs.onopen = () => {
      console.log('✅ Message Notification WebSocket connected successfully');
      setSignalingServer2(msgNotifWs);
      resetReconnectAttempts('msgNotif');
      setConnectionStates(prev => ({ ...prev, msgNotif: 'connected' }));

      // Debug: Verify the signaling server is set
      console.log('🔗 Message Notification WebSocket set in store, ready for message notifications');
    };

    msgNotifWs.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📨 Message Notification WebSocket received:', data);

        // Update heartbeat timestamp
        lastHeartbeat.current.msgNotif = Date.now();

        // Skip heartbeat responses
        if (data.type === 'pong') {
          console.log('💓 Received heartbeat pong from message notification WebSocket');
          return;
        }

        // Check for duplicates before processing
        if (isDuplicateMessage(data, 'MessageNotifWS')) {
          return;
        }

        // Handle different types of message notifications
        if (data.type === 'notif_from_messages' || data.type === 'notif_messages' || data.type === 'chat_message') {
          console.log('💬 Processing message notification:', data.type);

          // Create unique ID to prevent duplicates
          const messageId = data.id || `msg_${data.sender_id}_${Date.now()}`;

          const messageNotification = {
            id: messageId,
            content: data.message || data.notif || 'New message',
            message: data.message || data.notif || '',
            sender_id: data.sender_id,
            sender_name: data.sender_name || 'User',
            sender_image: data.sender_image || null,
            timestamp: data.timestamp || new Date().toISOString(),
            read: false,
          };

          addMessageNotification(messageNotification);
          console.log('✅ Message notification added to store:', messageNotification);
        } else if (data.type === 'notif' || data.type === 'notification') {
          console.log('🔔 Processing GENERAL NOTIFICATION on message WebSocket:', data.type, data);

          const notification = {
            id: data.id || Date.now(),
            content: data.notif || data.content || 'New notification',
            timestamp: data.timestamp || new Date().toISOString(),
            link: data.link,
            read: false,
            silent: false, // Ensure new notifications are NOT silent
            type: data.type,
            sender_id: data.sender_id,
            sender_name: data.sender_name || 'System',
            sender_image: data.sender_image || null
          };

          console.log('🔔 Adding GENERAL NOTIFICATION to store from message WebSocket:', notification);
          addNotification(notification);

          // Update NOTIFICATION count (not message count)
          updateNotificationCount(data.sender_id || userId.userId);
          console.log('📊 NOTIFICATION count updated for user:', data.sender_id || userId.userId);
        } else {
          console.log('ℹ️ Received non-message notification:', data.type);
        }
      } catch (error) {
        console.error('❌ Error processing message notification:', error);
      }
    };

    msgNotifWs.onclose = (event) => {
      console.log('Message Notification WebSocket closed:', event.code, event.reason);
      setConnectionStates(prev => ({ ...prev, msgNotif: 'disconnected' }));

      // Only attempt reconnection if it wasn't a clean close
      if (event.code !== 1000 && userId.userId) {
        scheduleReconnect('msgNotif', createMessageNotificationWebSocket);
      }
    };

    msgNotifWs.onerror = (error) => {
      console.error('Message Notification WebSocket error:', error);
      setConnectionStates(prev => ({ ...prev, msgNotif: 'error' }));
    };

    return msgNotifWs;
  }, [userId.userId, setSignalingServer2, addMessageNotification, addNotification, updateNotificationCount, resetReconnectAttempts, scheduleReconnect, isDuplicateMessage]);

  // Create Chat WebSocket
  const createChatWebSocket = useCallback(() => {
    if (!userId.userId) return;

    // Prevent duplicate connections
    if (chatWsRef.current && chatWsRef.current.readyState === WebSocket.OPEN) {
      console.log('Chat WebSocket already connected, skipping creation');
      return chatWsRef.current;
    }

    // Close existing connection if it exists
    if (chatWsRef.current) {
      console.log('Closing existing chat WebSocket before creating new one');
      chatWsRef.current.close(1000, 'Creating new connection');
    }

    const apiUrl = process.env.REACT_APP_API_URL_SOKET || '';
    console.log('Creating Chat WebSocket connection...');

    const chatWs = new WebSocket(`${apiUrl}ws/chat/${userId.userId}/0/`);
    chatWsRef.current = chatWs;

    chatWs.onopen = () => {
      console.log('Chat WebSocket connected successfully');
      chatWs.send(JSON.stringify({ type: "request_conversation_users_history" }));
      resetReconnectAttempts('chat');
    };

    chatWs.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('Chat WebSocket received:', data);

        // Update heartbeat timestamp
        lastHeartbeat.current.chat = Date.now();

        // Skip heartbeat responses
        if (data.type === 'pong') {
          console.log('💓 Received heartbeat pong from chat WebSocket');
          return;
        }

        if (data.type === "chat_message") {
          // Check for duplicates before processing
          if (isDuplicateMessage(data, 'ChatWS')) {
            return;
          }

          // Only handle chat messages that are actual new messages (not notifications)
          // Check if this is a real-time message by checking if we're not on the chat page
          // or if this message is from a different conversation
          const currentPath = window.location.pathname;
          const isOnChatPage = currentPath === '/chat';

          // Create unique ID to prevent duplicates
          const messageId = data.id || `chat_${data.sender_id}_${Date.now()}`;

          // Only add message notification if user is not actively in chat with this sender
          // This prevents duplicate notifications when user is already viewing the conversation
          if (!isOnChatPage) {
            const newMessage = {
              id: messageId,
              content: `New message from ${data.sender_name || 'someone'}: ${data.message ? data.message.substring(0, 30) + (data.message.length > 30 ? '...' : '') : 'New message'}`,
              message: data.message || '',
              sender_id: data.sender_id,
              sender_name: data.sender_name || 'User',
              sender_image: data.sender_image || null,
              timestamp: data.timestamp || new Date().toISOString(),
              read: false,
            };

            // Ensure the message has a valid timestamp
            if (!newMessage.timestamp) {
              newMessage.timestamp = new Date().toISOString();
            }

            console.log('💬 Adding chat message notification:', newMessage);
            addMessageNotification(newMessage);
          }
        } else if (data.type === "receiver_ring") {
          handleIncomingCallMessage(data, 'Chat');
        }
      } catch (error) {
        console.error('Error processing chat message:', error);
      }
    };

    chatWs.onclose = (event) => {
      console.log('Chat WebSocket closed:', event.code, event.reason);
      setConnectionStates(prev => ({ ...prev, chat: 'disconnected' }));

      // Only attempt reconnection if it wasn't a clean close
      if (event.code !== 1000 && userId.userId) {
        scheduleReconnect('chat', createChatWebSocket);
      }
    };

    chatWs.onerror = (error) => {
      console.error('Chat WebSocket error:', error);
      setConnectionStates(prev => ({ ...prev, chat: 'error' }));
    };

    return chatWs;
  }, [userId.userId, addMessageNotification, handleIncomingCallMessage, resetReconnectAttempts, scheduleReconnect, isDuplicateMessage]);

  // Create Ring WebSocket
  const createRingWebSocket = useCallback(() => {
    if (!userId.userId) return;

    // Prevent duplicate connections
    if (ringWsRef.current && ringWsRef.current.readyState === WebSocket.OPEN) {
      console.log('Ring WebSocket already connected, skipping creation');
      return ringWsRef.current;
    }

    // Close existing connection if it exists
    if (ringWsRef.current) {
      console.log('Closing existing ring WebSocket before creating new one');
      ringWsRef.current.close(1000, 'Creating new connection');
    }

    const apiUrl = process.env.REACT_APP_API_URL_SOKET || '';
    console.log('Creating Ring WebSocket connection...');

    const ringWs = new WebSocket(`${apiUrl}ws/ring/${userId.userId}/`);
    ringWsRef.current = ringWs;

    ringWs.onopen = () => {
      console.log('Ring WebSocket connected successfully');
      setSignalingServer3(ringWs);
      resetReconnectAttempts('ring');
    };

    ringWs.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('Ring WebSocket received:', data);

        // Update heartbeat timestamp
        lastHeartbeat.current.ring = Date.now();

        // Skip heartbeat responses
        if (data.type === 'pong') {
          console.log('💓 Received heartbeat pong from ring WebSocket');
          return;
        }

        if (data.type === "receiver_ring" || data.type === "end_ring" || data.type === "call_accepted") {
          handleIncomingCallMessage(data, 'Ring');
        }
      } catch (error) {
        console.error('Error processing ring message:', error);
      }
    };

    ringWs.onclose = (event) => {
      console.log('Ring WebSocket closed:', event.code, event.reason);
      setConnectionStates(prev => ({ ...prev, ring: 'disconnected' }));

      // Only attempt reconnection if it wasn't a clean close
      if (event.code !== 1000 && userId.userId) {
        scheduleReconnect('ring', createRingWebSocket);
      }
    };

    ringWs.onerror = (error) => {
      console.error('Ring WebSocket error:', error);
      setConnectionStates(prev => ({ ...prev, ring: 'error' }));
    };

    return ringWs;
  }, [userId.userId, setSignalingServer3, handleIncomingCallMessage, resetReconnectAttempts, scheduleReconnect]);

  // Main effect to initialize all WebSocket connections
  useEffect(() => {
    if (!userId.userId) {
      console.log('No user ID available, skipping WebSocket initialization');
      isInitialized.current = false;
      currentUserId.current = null;
      return;
    }

    // Prevent multiple initializations for the same user
    if (isInitialized.current && currentUserId.current === userId.userId) {
      console.log('🔌 WebSocketManager: Already initialized for user:', userId.userId);
      return;
    }

    // If user changed, clean up previous connections
    if (currentUserId.current && currentUserId.current !== userId.userId) {
      console.log('🔄 User changed, cleaning up previous connections');
      isInitialized.current = false;
    }

    console.log('🔌 WebSocketManager: Initializing WebSocket connections for user:', userId.userId);

    // Mark as initialized for this user
    isInitialized.current = true;
    currentUserId.current = userId.userId;

    // Create all WebSocket connections
    const notifWs = createNotificationWebSocket();
    const msgNotifWs = createMessageNotificationWebSocket();
    const chatWs = createChatWebSocket();
    const ringWs = createRingWebSocket();

    // Fetch initial unread notifications/messages once user is known
    try {
      initializeNotifications?.();
      fetchUnreadNotifications?.();
    } catch (e) {
      console.warn('Failed to initialize unread notifications:', e);
    }

    // Cleanup function
    return () => {
      console.log('🧹 Cleaning up WebSocket connections');
      isInitialized.current = false;
      currentUserId.current = null;

      // Clear all reconnection timers
      Object.keys(reconnectTimers.current).forEach(type => {
        clearReconnectTimer(type);
      });

      // Close all WebSocket connections
      if (notifWs && notifWs.readyState === WebSocket.OPEN) {
        notifWs.close(1000, 'Component unmounting');
      }
      if (msgNotifWs && msgNotifWs.readyState === WebSocket.OPEN) {
        msgNotifWs.close(1000, 'Component unmounting');
      }
      if (chatWs && chatWs.readyState === WebSocket.OPEN) {
        chatWs.close(1000, 'Component unmounting');
      }
      if (ringWs && ringWs.readyState === WebSocket.OPEN) {
        ringWs.close(1000, 'Component unmounting');
      }
    };
  }, [userId.userId]); // Removed WebSocket creation functions from dependencies

  // Start simple heartbeat monitoring - just to keep connections alive
  useEffect(() => {
    if (!userId.userId) return;

    console.log('💓 WebSocketManager: Starting simple heartbeat monitoring');

    const startHeartbeat = () => {
      heartbeatInterval.current = setInterval(() => {
        console.log('💓 Heartbeat: Checking connection status');

        // Just send ping to keep connections alive, don't reconnect aggressively
        const connections = [
          { ref: wsRef, type: 'notif' },
          { ref: notifMsgWsRef, type: 'msgNotif' },
          { ref: chatWsRef, type: 'chat' },
          { ref: ringWsRef, type: 'ring' }
        ];

        connections.forEach(({ ref, type }) => {
          const ws = ref.current;
          if (ws && ws.readyState === WebSocket.OPEN) {
            try {
              ws.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
              console.log(`💓 Sent heartbeat ping to ${type} WebSocket`);
            } catch (error) {
              console.error(`❌ Error sending heartbeat to ${type}:`, error);
            }
          } else {
            console.log(`⚠️ ${type} WebSocket not connected (state: ${ws?.readyState || 'null'})`);
          }
        });
      }, 300000); // Check every 5 minutes - much less aggressive
    };

    startHeartbeat();

    return () => {
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
      }
    };
  }, [userId.userId]);

  // Handle page visibility changes - TEMPORARILY DISABLED to prevent infinite reconnections
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && userId.userId) {
        console.log('📱 Page became visible - visibility change reconnection temporarily disabled');
        // TODO: Re-enable with better logic after fixing the infinite loop
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also check on window focus
    const handleFocus = () => {
      if (userId.userId) {
        console.log('🔍 Window focused - focus reconnection temporarily disabled');
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [userId.userId]); // Removed WebSocket creation functions from dependencies

  // Set up DOM ready listener
  useEffect(() => {
    if (!isDomReady.current) {
      const handleDomReady = () => {
        isDomReady.current = true;
        
        // Initialize audio before processing pending calls
        initializeAudio();
        
        // Process any pending calls
        pendingCallNotifications.current.forEach(data => {
          setActiveCall(data);
          
          // Try to play ringtone for pending calls
          if (isEnabled) {
            play()
              .then(() => console.log('Ring sound playing for pending call'))
              .catch(err => console.error('Error playing ringtone for pending call:', err));
          }
        });
        
        pendingCallNotifications.current = [];
      };
      
      document.addEventListener('DOMContentLoaded', handleDomReady);
      window.addEventListener('load', handleDomReady);
      
    return () => {
        document.removeEventListener('DOMContentLoaded', handleDomReady);
        window.removeEventListener('load', handleDomReady);
      };
    }
  }, [initializeAudio, isEnabled, play]);

  // Request notification permissions
  useEffect(() => {
    const requestNotificationPermission = async () => {
      if ('Notification' in window && Notification.permission === 'default') {
        try {
          await Notification.requestPermission();
                } catch (error) {
          console.error('Error requesting notification permission:', error);
        }
      }
    };

    requestNotificationPermission();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stop(); // Stop the ringtone
      
      setActiveCall(null);
      pendingCallNotifications.current = [];
      sessionStorage.removeItem('incomingCallData');
    };
  }, [stop]);

  return (
    <>
      {activeCall && (
        <CallModal
          callData={activeCall}
          onAccept={() => handleAcceptCall(activeCall)}
          onDecline={handleDeclineCall}
        />
      )}
    </>
  );
};

export default WebSocketManager; 